import { systemClock } from '@reos/config';
import { apiClient, baseUrl } from './api.js';
import { unwrap } from './api-error.js';

/**
 * Krok UI3 — statistiky inzerce a report pro majitele u zakazky. Data pocita
 * `@reos/finance` (`buildOwnerReportData`, SR3) - tenhle klient je jen tenky
 * obal nad `GET /v1/cases/:id/advertising-performance` (odpoved zatim
 * `zod.any()`, DTO psane rucne stejne jako u `costs-api.ts`).
 */

export interface DailyViewRow {
  readonly listingId: string;
  readonly portal: string;
  readonly statDate: string;
  readonly views: number;
}

export interface PortalPerformanceRow {
  readonly portal: string;
  readonly costTotal: string;
  readonly currency: string;
  readonly views: number;
  readonly reactions: number;
  readonly costPerView: string | null;
  readonly costPerReaction: string | null;
}

export interface AdvertisingPerformanceRecommendation {
  readonly portal: string;
  readonly code: 'no_reaction_despite_spend' | 'no_views_despite_spend';
}

type Section<T> = { readonly available: true; readonly data: T } | { readonly available: false; readonly reason: string };

export interface AdvertisingPerformanceDto {
  readonly caseId: string;
  readonly caseTitle: string;
  readonly periodFrom: string;
  readonly periodTo: string;
  readonly dailyViews: readonly DailyViewRow[];
  readonly traffic: Section<{ readonly byPortal: readonly { readonly portal: string; readonly views: number }[] }>;
  readonly performance: Section<{
    readonly byPortal: readonly PortalPerformanceRow[];
    readonly viewingsTotal: { readonly costTotal: string; readonly currency: string; readonly viewings: number; readonly costPerViewing: string | null } | null;
    readonly recommendations: readonly AdvertisingPerformanceRecommendation[];
  }>;
  readonly reactions: Section<{ readonly total: number; readonly byPortal: readonly { readonly portal: string; readonly count: number }[] }>;
  readonly viewings: Section<{ readonly total: number }>;
}

export interface AdvertisingPerformancePeriod {
  readonly periodFrom: string;
  readonly periodTo: string;
}

/** Vychozi obdobi karty: poslednich 30 dni (vcetne dneska). */
export function lastThirtyDaysPeriod(today: Date = systemClock.now()): AdvertisingPerformancePeriod {
  const to = today.toISOString().slice(0, 10);
  const fromDate = new Date(today);
  fromDate.setDate(fromDate.getDate() - 29);
  return { periodFrom: fromDate.toISOString().slice(0, 10), periodTo: to };
}

export async function fetchAdvertisingPerformance(
  caseId: string,
  period: AdvertisingPerformancePeriod,
): Promise<AdvertisingPerformanceDto> {
  return (await unwrap(
    apiClient.GET('/v1/cases/{id}/advertising-performance', {
      params: { path: { id: caseId }, query: period },
    }),
  )) as AdvertisingPerformanceDto;
}

/** Adresa HTML nahledu reportu — patri do `href`/`window.open`, ne do fetch (viz exports-api.ts). */
export function ownerReportPreviewUrl(caseId: string, period: AdvertisingPerformancePeriod): string {
  const search = new URLSearchParams({ periodFrom: period.periodFrom, periodTo: period.periodTo });
  return `${baseUrl}/v1/cases/${caseId}/owner-report/preview?${search.toString()}`;
}

/**
 * Krok NAP2 bod A6 — adresa PDF reportu pro majitele. TATAZ cesta, jakou uz
 * pouziva planovac ve workeru (P1EXP3): `GET …/owner-report/preview.pdf`
 * (Gotenberg). Zadna druha cesta k PDF — jen `href` na existujici routu,
 * stejne jako u HTML nahledu vyse.
 */
export function ownerReportPdfUrl(caseId: string, period: AdvertisingPerformancePeriod): string {
  const search = new URLSearchParams({ periodFrom: period.periodFrom, periodTo: period.periodTo });
  return `${baseUrl}/v1/cases/${caseId}/owner-report/preview.pdf?${search.toString()}`;
}

export interface ImportPortalStatsResult {
  readonly imported: number;
}

export async function importPortalStatsCsv(csv: string): Promise<ImportPortalStatsResult> {
  return (await unwrap(apiClient.POST('/v1/listing/portal-stats/import', { body: { csv } }))) as ImportPortalStatsResult;
}

/**
 * Krok SR3b — NASTAVENI AUTOMATICKEHO REPORTU MAJITELI u zakazky.
 *
 * Rozhodovani („kdy pristi odeslani", „posila se vubec") zije v domene
 * (`@reos/finance`, `owner-report-period.ts`) a prijde HOTOVE v DTO —
 * `nextSendAt` tady NIC nepocita, jinak by v adminu vznikla druha pravda
 * o periode.
 */
export interface OwnerReportSettingsDto {
  readonly caseId: string;
  readonly frequencyDays: 7 | 14 | 30;
  readonly autoSend: boolean;
  /** Kdy se majitel odhlasil odkazem v reportu; `null` = odhlasen neni. */
  readonly unsubscribedAt: string | null;
  /** Kdy report nejdriv odejde; `null` = neposila se (vypnuto/odhlaseno). */
  readonly nextSendAt: string | null;
  readonly lastSentAt: string | null;
}

/** Periody, ktere smi makler nastavit — tytez, jake pousti API i CHECK v DB. */
export const OWNER_REPORT_FREQUENCY_CHOICES: readonly (7 | 14 | 30)[] = [7, 14, 30];

export async function fetchOwnerReportSettings(caseId: string): Promise<OwnerReportSettingsDto> {
  return (await unwrap(
    apiClient.GET('/v1/cases/{id}/owner-report/settings', { params: { path: { id: caseId } } }),
  )) as OwnerReportSettingsDto;
}

export async function saveOwnerReportSettings(
  caseId: string,
  body: { readonly frequencyDays: 7 | 14 | 30; readonly autoSend: boolean },
): Promise<OwnerReportSettingsDto> {
  return (await unwrap(
    apiClient.PUT('/v1/cases/{id}/owner-report/settings', { params: { path: { id: caseId } }, body }),
  )) as OwnerReportSettingsDto;
}

/** Odhlaseni odvolal majitel (rekl to makleri) — vyslovna akce, ne prepinac. */
export async function resubscribeOwnerReport(caseId: string): Promise<OwnerReportSettingsDto> {
  return (await unwrap(
    apiClient.POST('/v1/cases/{id}/owner-report/resubscribe', { params: { path: { id: caseId } } }),
  )) as OwnerReportSettingsDto;
}
